import {
  selectAllItems,
  selectAnyRequestStatus,
} from '../../entities/product/model';
import moment from 'moment';
import 'moment/locale/ru';
import MainLayout from '@shared/ui/main-layout';
import { EmptyState } from '../../shared/ui/empty-state';
import {
  DashboardUserItem,
  DashboardPriceItem,
  DashboardCartTable,
  DashboardOrderItem,
  DashboardCompanyItem,
  DashboardCatalogCarousel,
  DashboardItem,
} from '@widgets/dashboard';
import {
  selectAllOrders,
  selectOrdersStatus,
} from '../../entities/order/model';
import {
  selectCartWithCalculatedPrices,
  selectCartStatus,
} from '../../entities/cart/model';
import { selectPrices } from '../../entities/price/model';
import { selectUsers, selectUsersStatus } from '../../entities/user/model';
import {
  selectLegalEntities,
  selectLegalEntityStatus,
} from '../../entities/legal-entity/model';
import useDashboardEffects from './hooks/useDashboardEffects';
import { useAppSelector } from '../../app/store/store';
import { Can } from '@shared/lib/ability';
import { useMe } from '../Area/Area';
moment.locale('ru');

// Внутренние FMeter-роли: для них «Компании» в виджете имеет смысл —
// это пул клиентов на /companies. Для клиентской стороны (OWNER/USER)
// тот же виджет показывает их собственные юр. лица, поэтому ведём
// уже на их карточку компании.
const FMETER_INTERNAL_GROUPS = [
  'MANAGER',
  'OFFICE_MANAGER',
  'SALES_DIRECTOR',
  'admins',
  'HD_HEAD',
  'HD_ENGINEER',
  'HD_SERVICE_ENGINEER',
];

const Dashboard = () => {
  const { me } = useMe();
  const companies = useAppSelector(selectLegalEntities);
  const catalogItems = useAppSelector(selectAllItems);
  const orders = useAppSelector(selectAllOrders);
  const cart = useAppSelector(selectCartWithCalculatedPrices);
  const managers = useAppSelector(selectUsers);
  const prices = useAppSelector(selectPrices);
  const catalogStatus = useAppSelector((state) =>
    selectAnyRequestStatus(state, 'getItems')
  );
  const ordersStatus = useAppSelector(selectOrdersStatus);
  const getPriceListStatus = useAppSelector((state) =>
    selectAnyRequestStatus(state, 'getPriceList')
  );
  const fetchManagersStatus = useAppSelector(selectUsersStatus);
  const companiesStatus = useAppSelector(selectLegalEntityStatus);
  const cartStatus = useAppSelector(selectCartStatus);

  useDashboardEffects();

  // Куда ведёт виджет «Юр. лица» (исторически назван «Компании»):
  //   • FMeter-staff → /companies (список клиентского пула — их рабочая
  //     страница);
  //   • клиент с привязанной компанией → /company/{его id} (там есть
  //     таб с юр. лицами);
  //   • клиент-без-компании сюда не попадёт — у него нет
  //     `view_legalentity`, гейт `<Can I="view" a="legalentity">`
  //     схлопывает виджет.
  const isFMeterStaff =
    Boolean(me?.is_staff) ||
    Boolean(me?.is_superuser) ||
    (me?.groups ?? []).some((g) => FMETER_INTERNAL_GROUPS.includes(g));
  const legalEntitiesLink = isFMeterStaff
    ? '/companies'
    : me?.company?.id
    ? `/company/${me.company.id}`
    : '/companies';

  return (
    <MainLayout>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-6">
        <Can I="view" a="card">
          <div className="col-span-1 sm:col-span-2 lg:col-span-8">
            <DashboardItem
              status={catalogStatus}
              title={'Каталог'}
              link={'/catalog'}
              type="catalog"
            >
              <DashboardCatalogCarousel items={catalogItems} />
            </DashboardItem>
          </div>
        </Can>

        <Can I="viewmanagers" a="username">
          <div className="col-span-1 lg:col-span-4">
            <DashboardItem
              status={fetchManagersStatus}
              title={'Менеджеры'}
              link={'/managers'}
              type="managers"
            >
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {managers?.slice(0, 4).map((manager) => (
                  <DashboardUserItem
                    key={manager.id}
                    link={'/managers'}
                    state={{ manager: manager, type: 'edit' }}
                    item={manager}
                  />
                ))}
              </div>
            </DashboardItem>
          </div>
        </Can>

        <Can I="view" a="pricelist">
          <div className="col-span-1 lg:col-span-4">
            <DashboardItem
              status={getPriceListStatus}
              title={'Прайсы'}
              link={'/prices'}
              type="prices"
            >
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {prices && prices.length ? (
                  prices
                    .slice(0, 3)
                    .map((item) => (
                      <DashboardPriceItem key={item.id} price={item} />
                    ))
                ) : (
                  <EmptyState message="Прайсов не найдено" />
                )}
              </div>
            </DashboardItem>
          </div>
        </Can>

        {/*
          Корзина — фича клиентской стороны (закупка). Внутреннему
          FMeter-юзеру (admin/manager/HD-…) её показывать незачем:
          данных для покупки у его аккаунта нет, виджет всегда пустой
          и только засоряет дашборд. CASL-гейт `view_cart` через
          `manage all` пропускает суперюзера, поэтому добавляем явный
          рендер-фильтр по группе.
        */}
        {!isFMeterStaff && (
          <Can I="view" a="cart">
            <div className="col-span-1 lg:col-span-4">
              <DashboardItem
                status={cartStatus}
                title={'Корзина'}
                link={'/cart'}
                type="cart"
              >
                <DashboardCartTable cart={cart} />
              </DashboardItem>
            </div>
          </Can>
        )}

        <Can I="view" a="legalentity">
          <div className="col-span-1 lg:col-span-4">
            <DashboardItem
              status={companiesStatus}
              title={isFMeterStaff ? 'Компании' : 'Юр. лица'}
              link={legalEntitiesLink}
              type="companies"
            >
              <div className="space-y-2 max-h-[300px] overflow-y-auto ">
                {companies?.slice(0, 5).map((legalEntity) => (
                  <DashboardCompanyItem
                    item={legalEntity}
                    key={legalEntity.id}
                    to={legalEntitiesLink}
                  />
                ))}
              </div>
            </DashboardItem>
          </div>
        </Can>

        <Can I="view" a="order">
          <div className="col-span-1 lg:col-span-4">
            <DashboardItem
              status={ordersStatus}
              title={'Заказы'}
              link={'/orders'}
              type="orders"
            >
              <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto">
                {orders && orders.length ? (
                  orders
                    .slice(0, 8)
                    ?.map((order) => (
                      <DashboardOrderItem key={order.id} order={order} />
                    ))
                ) : (
                  <div className="col-span-full">
                    <EmptyState message="Заказов не найдено" />
                  </div>
                )}
              </div>
            </DashboardItem>
          </div>
        </Can>
      </div>
    </MainLayout>
  );
};

export default Dashboard;
